/* ──────────────────────────────────────────────────────────────────────────
   The maths behind the charts.

   No charting library, for the same reason numero-creators and oga-reporting
   do not have one: a line, a donut and a bar are a handful of coordinates, and
   a dependency that draws them brings its own opinions about typography,
   tooltips and theming that then have to be argued with. These return numbers;
   the components decide what they look like.

   Everything here is pure, so it can be tested without a browser.
   ────────────────────────────────────────────────────────────────────────── */

export type Point = { x: number; y: number };

/**
 * A smooth line through a set of points.
 *
 * Catmull-Rom converted to cubic béziers. `tension` at 0 gives straight
 * segments; the default is enough to read as a curve without inventing peaks
 * the data does not have, which is the failure mode of a prettier spline.
 */
export function smoothPath(points: Point[], tension = 0.72): string {
  if (points.length === 0) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  if (points.length === 2) return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;

  let path = `M ${points[0].x} ${points[0].y}`;
  for (let index = 0; index < points.length - 1; index += 1) {
    const previous = points[index - 1] ?? points[index];
    const current = points[index];
    const next = points[index + 1];
    const after = points[index + 2] ?? next;

    const control1 = {
      x: current.x + ((next.x - previous.x) / 6) * tension,
      y: current.y + ((next.y - previous.y) / 6) * tension,
    };
    const control2 = {
      x: next.x - ((after.x - current.x) / 6) * tension,
      y: next.y - ((after.y - current.y) / 6) * tension,
    };
    path += ` C ${control1.x} ${control1.y} ${control2.x} ${control2.y} ${next.x} ${next.y}`;
  }
  return path;
}

/**
 * Values to coordinates inside a box.
 *
 * `null` is a hole, not a zero — a day with no data and a day with no clicks
 * are different claims, and drawing the first as the second invents a floor.
 */
export function seriesPoints({
  values,
  width,
  height,
  padding = 2,
  max,
}: {
  values: (number | null)[];
  width: number;
  height: number;
  padding?: number;
  max?: number;
}): (Point | null)[] {
  const numbers = values.filter((value): value is number => value !== null);
  const peak = max ?? Math.max(1, ...numbers);
  const inner = { width: width - padding * 2, height: height - padding * 2 };
  const step = values.length > 1 ? inner.width / (values.length - 1) : 0;

  return values.map((value, index) =>
    value === null
      ? null
      : {
          x: padding + (values.length === 1 ? inner.width / 2 : index * step),
          y: padding + inner.height - (value / peak) * inner.height,
        }
  );
}

/** The unbroken runs of a series, so a hole becomes a gap rather than a dive. */
export function segments(points: (Point | null)[]): Point[][] {
  const runs: Point[][] = [];
  let run: Point[] = [];
  for (const point of points) {
    if (point) run.push(point);
    else if (run.length) {
      runs.push(run);
      run = [];
    }
  }
  if (run.length) runs.push(run);
  return runs;
}

export type Arc<T> = T & {
  /** The `d` of a donut segment, ready for a path. */
  path: string;
  share: number;
  /** Where a label sits without overlapping its neighbours. */
  mid: Point;
};

/**
 * A donut, as one path per slice.
 *
 * Slices below a whisker of the total are still drawn: dropping them makes the
 * ring not add up, and somebody will check.
 */
export function donutArcs<T extends { value: number }>(
  entries: T[],
  { size = 120, thickness = 18, gap = 0.012 }: { size?: number; thickness?: number; gap?: number } = {}
): Arc<T>[] {
  const total = entries.reduce((sum, entry) => sum + Math.max(0, entry.value), 0);
  if (total <= 0) return [];

  const centre = size / 2;
  const outer = centre - 1;
  const inner = outer - thickness;
  let angle = -Math.PI / 2;

  return entries.map((entry) => {
    const share = Math.max(0, entry.value) / total;
    const sweep = share * Math.PI * 2;
    // A gap between slices, unless the slice is the whole ring — in which case
    // a gap would leave a notch in something that has no divisions.
    const trim = entries.length > 1 ? Math.min(gap, sweep / 3) : 0;
    const start = angle + trim;
    const end = angle + sweep - trim;
    angle += sweep;

    const large = end - start > Math.PI ? 1 : 0;
    const point = (radius: number, at: number) => ({
      x: centre + radius * Math.cos(at),
      y: centre + radius * Math.sin(at),
    });
    const a = point(outer, start);
    const b = point(outer, end);
    const c = point(inner, end);
    const d = point(inner, start);

    return {
      ...entry,
      share,
      mid: point((outer + inner) / 2, (start + end) / 2),
      path:
        `M ${a.x} ${a.y} A ${outer} ${outer} 0 ${large} 1 ${b.x} ${b.y}` +
        ` L ${c.x} ${c.y} A ${inner} ${inner} 0 ${large} 0 ${d.x} ${d.y} Z`,
    };
  });
}

/** Period over period, as a fraction. Null when there is nothing to compare to. */
export function change(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null || previous === 0) return null;
  return (current - previous) / previous;
}
